import { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  Flex,
  Avatar,
  VStack,
  Select,
  Icon,
  Input,
  useToast,
  Spinner,
  SimpleGrid,
  Tooltip,
  Tag,
  TagLabel,
} from '@chakra-ui/react'
import { isEmail, isEmpty, isMobilePhone, isPostalCode } from 'validator'
import { phoneNumbers } from '../countries/PhoneDial'
import ModalInput from '../forms/modalForms/modalInput'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchJobList } from '../../api/classification/classification'
import { CustomDateInput } from '../forms/modalForms/customDate'
import CustomNumericInput from '../forms/modalForms/customNumericInput'
import ModalSelect from '../forms/modalForms/modalSelect'
import { UploadCloud02Icon, WarningIcon } from '../../assets/icons'
import ModalMultiSelect from '../forms/modalForms/modalMultiSelect'
import { getCodeByCountryName, countrySelectOptions } from '../countries/PhoneDial'
import { addEmployee, updateEmployee } from '../../api/workforce/workforce'
import handleToast from '../HandleToast'
import { fetchTeamList } from '../../api/workforce/workforce'
import ModalPhoneInput from '../forms/modalForms/modalPhoneInput'
import { parseLocalDate } from '../../helpers/dateUtils'
import { fetchEmployeeList } from '../../api/workforce/workforce'
import { useTranslation } from 'react-i18next'
import { parseDateOnly } from '../../helpers/parseDateOnly'
import { modal, button } from '../../themes/styles'
import CollapsibleSection from './CollapsibleSection'
import { getDocuments, createDocument, createDocumentFromTemplate } from '../../api/document/document'
import { Checkbox } from '@chakra-ui/react'
import { getAddEmployeePageSetup } from '../../api/settings/pageSetup'
import { AddEmployeePageSetupConfig } from '../../types/settings/pageSetupTypes'
import { createPathParticipant } from '../../api/paths/pathParticipants'
import { fetchPathList } from '../../api/paths/paths'
import SearchInput from '../tables/base/SearchInput'

const ADD_EMPLOYEE_FIELD_LABEL_KEYS: Record<string, string> = {
  first_name: 'common.fields.firstName',
  last_name: 'common.fields.lastName',
  full_name: 'workforce.people.fields.fullName',
  local_name: 'workforce.people.fields.localName',
  national_identification_number: 'workforce.people.fields.nationalIdNumber',
  personal_email: 'workforce.people.fields.personalEmail',
  education_level: 'workforce.people.fields.educationLevel',
  nationality: 'workforce.people.fields.nationality',
  country_of_residence: 'workforce.people.fields.countryOfResidence',
  phone_number: 'workforce.people.fields.phoneNumber',
  address: 'workforce.people.fields.address',
  city: 'workforce.people.fields.city',
  zip_code: 'workforce.people.fields.zipCode',
  date_of_birth: 'workforce.people.fields.dateOfBirth',
  gender: 'workforce.people.fields.gender',
  emergency_contact_email: 'workforce.people.fields.emergencyContactEmail',
  emergency_contact_phone: 'workforce.people.fields.emergencyContactNumber',
  emergency_contact_type: 'workforce.people.fields.emergencyContactType',
  emergency_contact_name: 'workforce.people.fields.emergencyContactName',
  work_email: 'workforce.people.fields.workEmail',
  full_job_title: 'workforce.people.fields.fullJobTitleShort',
  employee_number: 'workforce.people.fields.employeeNumber',
  department: 'workforce.people.fields.department',
  country_of_work: 'workforce.people.countryOfWork',
  teams: 'workforce.teams.teamName',
  current_position: 'workforce.people.fields.currentPosition',
  previous_position: 'workforce.people.fields.previousPosition',
  contract_type: 'workforce.mutations.fields.contractType',
  contract_duration: 'workforce.mutations.fields.contractDuration',
  start_date: 'workforce.people.fields.startDate',
  salary: 'workforce.people.fields.grossSalary',
  probation_end_date: 'workforce.people.fields.probationPeriodEndDate',
  temporary_contract_end_date: 'workforce.people.fields.temporaryContractEndDate',
  hours_per_week: 'workforce.mutations.fields.workHoursPerWeek',
  number_of_holidays: 'workforce.people.fields.holidaysPerYear',
  hours_in_office: 'workforce.mutations.fields.workHoursInOffice',
  hours_from_home: 'workforce.mutations.fields.workHoursFromHome',
  primary_evaluator: 'workforce.people.fields.primaryEvaluator',
  secondary_evaluator: 'workforce.people.fields.secondaryEvaluator',
  mentor: 'workforce.people.fields.mentor',
  career_start_date: 'workforce.people.fields.careerStartDate',
  last_day: 'workforce.people.deactivation.lastDayLabel',
}

const DEFAULT_ADD_EMPLOYEE_SETUP: AddEmployeePageSetupConfig = {
  fields: [
    { key: 'first_name', label: 'First name', section: 'personal', visible: true, required: true, locked: true },
    { key: 'last_name', label: 'Last name', section: 'personal', visible: true, required: true, locked: true },
    { key: 'full_name', label: 'Full name', section: 'personal', visible: true, required: false },
    { key: 'local_name', label: 'Local name', section: 'personal', visible: true, required: false },
    { key: 'national_identification_number', label: 'National ID number', section: 'personal', visible: true, required: false },
    { key: 'personal_email', label: 'Personal email', section: 'personal', visible: true, required: false },
    { key: 'education_level', label: 'Education level', section: 'personal', visible: true, required: false },
    { key: 'nationality', label: 'Nationality', section: 'personal', visible: true, required: false },
    { key: 'country_of_residence', label: 'Country of residence', section: 'personal', visible: true, required: false },
    { key: 'phone_number', label: 'Phone number', section: 'personal', visible: true, required: false },
    { key: 'address', label: 'Address', section: 'personal', visible: true, required: false },
    { key: 'city', label: 'City', section: 'personal', visible: true, required: false },
    { key: 'zip_code', label: 'Zip code', section: 'personal', visible: true, required: false },
    { key: 'date_of_birth', label: 'Date of birth', section: 'personal', visible: true, required: false },
    { key: 'gender', label: 'Gender', section: 'personal', visible: true, required: false },
    { key: 'emergency_contact_email', label: 'Emergency contact email', section: 'personal', visible: true, required: false },
    { key: 'emergency_contact_phone', label: 'Emergency contact number', section: 'personal', visible: true, required: false },
    { key: 'emergency_contact_type', label: 'Emergency contact type', section: 'personal', visible: true, required: false },
    { key: 'emergency_contact_name', label: 'Emergency contact name', section: 'personal', visible: true, required: false },
    { key: 'work_email', label: 'Work email', section: 'work', visible: true, required: true, locked: true },
    { key: 'full_job_title', label: 'Job title', section: 'work', visible: true, required: false },
    { key: 'employee_number', label: 'Employee number', section: 'work', visible: true, required: false },
    { key: 'department', label: 'Department', section: 'work', visible: true, required: false },
    { key: 'country_of_work', label: 'Country of work', section: 'work', visible: true, required: true },
    { key: 'teams', label: 'Teams', section: 'work', visible: true, required: false },
    { key: 'current_position', label: 'Current position', section: 'work', visible: true, required: false },
    { key: 'previous_position', label: 'Previous position', section: 'work', visible: true, required: false },
    { key: 'contract_type', label: 'Contract type', section: 'work', visible: true, required: false },
    { key: 'contract_duration', label: 'Contract duration', section: 'work', visible: true, required: false },
    { key: 'start_date', label: 'Start date', section: 'work', visible: true, required: true },
    { key: 'salary', label: 'Gross salary', section: 'work', visible: true, required: false },
    { key: 'probation_end_date', label: 'Probation end date', section: 'work', visible: true, required: false },
    { key: 'temporary_contract_end_date', label: 'Temporary contract end date', section: 'work', visible: true, required: false },
    { key: 'hours_per_week', label: 'Work hours per week', section: 'work', visible: true, required: false },
    { key: 'number_of_holidays', label: 'Holidays per year', section: 'work', visible: true, required: false },
    { key: 'hours_in_office', label: 'Work hours in office', section: 'work', visible: true, required: false },
    { key: 'hours_from_home', label: 'Work hours from home', section: 'work', visible: true, required: false },
    { key: 'primary_evaluator', label: 'Primary evaluator', section: 'work', visible: true, required: false },
    { key: 'secondary_evaluator', label: 'Secondary evaluator', section: 'work', visible: true, required: false },
    { key: 'mentor', label: 'Mentor', section: 'work', visible: true, required: false },
    { key: 'career_start_date', label: 'Career start date', section: 'work', visible: true, required: false },
    { key: 'last_day', label: 'Last day', section: 'work', visible: true, required: false },
  ],
}

const normalizeAddEmployeeSetup = (config?: AddEmployeePageSetupConfig | null): AddEmployeePageSetupConfig => {
  const configuredByKey = new Map((config?.fields || []).map((field) => [field.key, field]))
  return {
    fields: DEFAULT_ADD_EMPLOYEE_SETUP.fields.map((defaultField) => {
      const configuredField = configuredByKey.get(defaultField.key)
      const visible = defaultField.locked ? true : configuredField?.visible ?? defaultField.visible
      return {
        ...defaultField,
        visible,
        required: visible ? (defaultField.locked ? true : configuredField?.required ?? defaultField.required) : false,
      }
    }),
  }
}

interface EmployeeRecord {
  id: string | number
  full_name: string
  [key: string]: unknown
}

interface ManagerRecord {
  id: string
  first_name: string
  last_name: string
  profile_picture?: string | null
}

interface ManagerOption {
  value: string
  label: string
  profilePicture?: string
}

interface TemplateRecord {
  id: string | number
  title: string
  version?: string
  status?: string
  default_status?: string
  bulk_assigned?: boolean
}

interface PathRecord {
  id: string | number
  title: string
  description?: string
  published?: boolean
  participants?: Array<string | number>
}

interface PaginatedPathResponse {
  data?: PathRecord[]
  has_next?: boolean
}

interface AddEmployeesModalProps {
  isOpen: boolean
  onClose: () => void
  onAddEmployee?: (employee: EmployeeRecord) => void
  prefillData?: {
    first_name?: string
    last_name?: string
    work_email?: string
    personal_email?: string
    phone_number?: string
    country_of_work?: string
  }
  onSuccess?: (employee: EmployeeRecord) => void
}

const AddEmployeesModal: React.FC<AddEmployeesModalProps> = ({ isOpen, onClose, onAddEmployee, prefillData, onSuccess }) => {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const examplePlaceholder = (example: string) => t('common.placeholders.eg', { example })

  const [employee, setEmployee] = useState<{
    first_name: string
    last_name: string
    full_name: string
    local_name: string
    teams: string[]
    nationality: string
    country_of_residence: string
    national_identification_number: string
    country_of_work: string
    phone_country: string
    personal_email: string
    education_level: string
    work_email: string
    employee_number: string
    hours_per_week: number
    start_date: string
    career_start_date?: string
    city: string
    contract_type: string | null
    contract_duration: string | null
    number_of_holidays: number
    manager: string
    current_position: string
    previous_position: string
    department: string
    hours_in_office: number
    hours_from_home: number
    gender: string | null
    zip_code: string
    phone_number: string
    address: string
    date_of_birth?: string
    job_profiles: string[]
    profile_picture: File | null
    is_admin: boolean
    emergency_contact_email: string
    emergency_contact_name: string
    emergency_contact_phone: string
    emergency_contact_type: string
    emergency_phone_country: string
    primary_evaluator: string
    secondary_evaluator: string
    last_day?: string
    mentor: string
    full_job_title: string
    salary: number
    probation_end_date?: string | null
    temporary_contract_end_date?: string | null
  }>({
    first_name: '',
    last_name: '',
    full_name: '',
    local_name: '',
    teams: [],
    nationality: '',
    country_of_residence: '',
    national_identification_number: '',
    country_of_work: '',
    phone_country: '',
    personal_email: '',
    education_level: '',
    work_email: '',
    employee_number: '',
    hours_per_week: 0,
    start_date: '',
    career_start_date: '',
    contract_type: null,
    contract_duration: null,
    number_of_holidays: 0,
    manager: '',
    current_position: '',
    city: '',
    previous_position: '',
    department: 'OP',
    hours_in_office: 0,
    hours_from_home: 0,
    gender: null,
    phone_number: '',
    address: '',
    zip_code: '',
    date_of_birth: '',
    job_profiles: [],
    profile_picture: null,
    is_admin: false,
    emergency_contact_email: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_type: '',
    emergency_phone_country: '',
    primary_evaluator: '',
    secondary_evaluator: '',
    last_day: '',
    mentor: '',
    full_job_title: '',
    salary: 0,
    probation_end_date: '',
    temporary_contract_end_date: '',
  })

  const [, setPhoneCode] = useState('')
  const [emergencyPhoneCountry, setEmergencyPhoneCountry] = useState('')
  const [, setEmergencyPhoneCode] = useState('')
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    workEmail: '',
    dateOfBirth: '',
    personalEmail: '',
    emergencyContactEmail: '',
    emergencyContactNumber: '',
    phoneNumber: '',
    zipCode: '',
    startDate: '',
    careerStartDate: '',
    countryOfWork: '',
    salary: '',
    temporaryContractEndDate: '',
    probationEndDate: '',
  })

  // Prefill from candidate data when opening from inflow
  useEffect(() => {
    if (isOpen && prefillData) {
      setEmployee((prev) => ({
        ...prev,
        first_name: prefillData.first_name || prev.first_name,
        last_name: prefillData.last_name || prev.last_name,
        work_email: prefillData.work_email || prev.work_email,
        personal_email: prefillData.personal_email || prev.personal_email,
        phone_number: prefillData.phone_number || prev.phone_number,
        country_of_work: prefillData.country_of_work || prev.country_of_work,
      }))
    }
  }, [isOpen, prefillData])

  const [startDate, setStartDate] = useState<Date | null>(null)
  const [dob, setDob] = useState<Date | null>(null)
  const [, setLastDay] = useState<Date | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  interface JobPosition {
    id: string
    title: string
  }

  const [jobPositions, setJobPositions] = useState<JobPosition[]>([])
  interface Team {
    id: string
    name: string
  }

  const [teams, setTeams] = useState<Team[]>([])

  const addEmployeeSetupQuery = useQuery({
    queryKey: ['page-setup', 'add-employee'],
    queryFn: getAddEmployeePageSetup,
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  })

  const fieldConfigMap = useMemo(() => {
    const normalizedSetup = normalizeAddEmployeeSetup(addEmployeeSetupQuery.data?.config)
    return new Map(normalizedSetup.fields.map((field) => [field.key, field]))
  }, [addEmployeeSetupQuery.data?.config])

  const isFieldVisible = (fieldKey: string) => fieldConfigMap.get(fieldKey)?.visible ?? true
  const isFieldRequired = (fieldKey: string) => fieldConfigMap.get(fieldKey)?.required ?? false
  const getFieldLabel = (fieldKey: string) => t(ADD_EMPLOYEE_FIELD_LABEL_KEYS[fieldKey] || fieldKey)



  // --- Documents state ---
  const [uploadedDocs, setUploadedDocs] = useState<{ file: File; title: string; status: string }[]>([])
  const [checkedTemplates, setCheckedTemplates] = useState<Set<string>>(new Set())
  const [templateStatusOverrides, setTemplateStatusOverrides] = useState<Record<string, string>>({})
  const [pathSearchTerm, setPathSearchTerm] = useState('')
  const [pathSearchResetKey, setPathSearchResetKey] = useState(0)
  const [selectedPaths, setSelectedPaths] = useState<Record<string, PathRecord>>({})
  const [sectionsOpen, ] = useState<{ personal: boolean; work: boolean; documents: boolean; paths: boolean }>({
    personal: true,
    work: true,
    documents: true,
    paths: true,
  })

  const templatesQuery = useQuery({
    queryKey: ['org-templates-for-employee'],
    queryFn: () => getDocuments({ doc_type: 'templates', page: 1, page_size: 100, search: '', fields: 'id,title,version,status,bulk_assigned,default_status', filters: { bulk_assigned: ['true'] } }),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  })

  const templatesList = useMemo(() => {
    const raw = templatesQuery.data
    if (Array.isArray(raw)) return raw
    if (Array.isArray(raw?.data)) return raw.data
    if (Array.isArray(raw?.results)) return raw.results
    return []
  }, [templatesQuery.data])

  // Pre-check all templates whenever the modal opens.
  useEffect(() => {
    if (isOpen && templatesList.length > 0) {
      setCheckedTemplates(new Set(templatesList.map((t: TemplateRecord) => String(t.id))))
    }
  }, [isOpen, templatesList])

  const pathTemplatesQuery = useInfiniteQuery({
    queryKey: ['addEmployees-paths', pathSearchTerm],
    queryFn: ({ pageParam }) =>
      fetchPathList({
        page: pageParam as number,
        pageSize: 12,
        search: pathSearchTerm,
        filters: {
          published: true,
        },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  })

  const availablePaths = useMemo(() => {
    return (pathTemplatesQuery.data?.pages ?? []).flatMap((page: PaginatedPathResponse) =>
      Array.isArray(page?.data) ? page.data : [],
    ) as PathRecord[]
  }, [pathTemplatesQuery.data?.pages])

  const selectedPathList = useMemo(() => Object.values(selectedPaths), [selectedPaths])

  const handlePathScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60

    if (nearBottom && pathTemplatesQuery.hasNextPage && !pathTemplatesQuery.isFetchingNextPage) {
      pathTemplatesQuery.fetchNextPage()
    }
  }, [pathTemplatesQuery])

  const togglePathSelection = (path: PathRecord, isChecked: boolean) => {
    const pathId = String(path.id)
    setSelectedPaths((prev) => {
      if (!isChecked) {
        const next = { ...prev }
        delete next[pathId]
        return next
      }

      return {
        ...prev,
        [pathId]: path,
      }
    })
  }

  const [managerSearch, setManagerSearch] = useState('')
  const [selectedPrimaryManager, setSelectedPrimaryManager] = useState<ManagerRecord | null>(null)
  const [selectedSecondaryManager, setSelectedSecondaryManager] = useState<ManagerRecord | null>(null)
  const [selectedMentorManager, setSelectedMentorManager] = useState<ManagerRecord | null>(null)
  const [teamsSearch, setTeamsSearch] = useState('')
  const [teamsPage, setTeamsPage] = useState(1)

  const jobPositionsQuery = useQuery({
    queryKey: ['addEmployees-jobPositions'],
    queryFn: () => fetchJobList({ search: '', page: 1, pageSize: 50, fields: 'id,title' }),
    enabled: isOpen,
  })

  // populate local state when modal opens and query returns
  useEffect(() => {
    if (jobPositionsQuery.data) setJobPositions(jobPositionsQuery.data?.data ?? [])
  }, [jobPositionsQuery.data])

  const teamsQuery = useQuery({
    queryKey: ['addEmployees-teams', teamsPage, teamsSearch],
    queryFn: () => fetchTeamList({ search: teamsSearch, page: teamsPage, pageSize: 50, filters: {}, fields: 'id,name' }),
    enabled: isOpen,
  })

  useEffect(() => {
    if (teamsQuery.data) setTeams(teamsQuery.data?.data ?? [])
  }, [teamsQuery.data])



  // Query to fetch potential managers
  // Query to fetch potential managers for each field
  // Single managers query shared by the three evaluator fields. It runs only when there's a search term.
  const managersQuery = useQuery({
    queryKey: ['addEmployees-managers', managerSearch],
    queryFn: () =>
      fetchEmployeeList({
        search: managerSearch,
        page: 1,
        pageSize: 50,
        filters: {},
        minimal: true,
      }),
    enabled: isOpen && managerSearch.trim().length > 0,
  })

  // Get formatted manager list for dropdown
  const managerOptions: ManagerOption[] =
    managersQuery.data?.data?.map((manager: ManagerRecord) => ({
      value: manager.id,
      label: `${manager.first_name} ${manager.last_name}`,
      profilePicture: manager.profile_picture,
    })) || []

  // Ensure each select's currently chosen manager is included in its options
  const optionsPrimary: ManagerOption[] = selectedPrimaryManager
    ? [
        { value: selectedPrimaryManager.id, label: `${selectedPrimaryManager.first_name} ${selectedPrimaryManager.last_name}`, profilePicture: selectedPrimaryManager.profile_picture ?? undefined },
        ...managerOptions.filter((opt) => opt.value !== selectedPrimaryManager.id),
      ]
    : managerOptions

  const optionsSecondary: ManagerOption[] = selectedSecondaryManager
    ? [
        { value: selectedSecondaryManager.id, label: `${selectedSecondaryManager.first_name} ${selectedSecondaryManager.last_name}`, profilePicture: selectedSecondaryManager.profile_picture ?? undefined },
        ...managerOptions.filter((opt) => opt.value !== selectedSecondaryManager.id),
      ]
    : managerOptions

  const optionsMentor: ManagerOption[] = selectedMentorManager
    ? [
        { value: selectedMentorManager.id, label: `${selectedMentorManager.first_name} ${selectedMentorManager.last_name}`, profilePicture: selectedMentorManager.profile_picture ?? undefined },
        ...managerOptions.filter((opt) => opt.value !== selectedMentorManager.id),
      ]
    : managerOptions

  // No pagination for manager searches: queries trigger only when user types a search term

  useEffect(() => {
    setTeamsPage(1)
  }, [teamsSearch])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setEmployee((prev) => ({
      ...prev,
      [name]: name === 'current_position' || name === 'previous_position' ? value : value,
    }))
    setErrors({ ...errors, [name]: '' })

    if (name === 'phone_country') {
      setEmployee((prev) => ({
        ...prev,
        phone_country: value.toUpperCase(),
      }))
      const selectedCountry = phoneNumbers.find(
        (country_of_residence) => country_of_residence.code.toLowerCase() === value.toLowerCase(),
      )
      if (selectedCountry) {
        setPhoneCode(selectedCountry.dial_code)
        setEmployee((prev) => ({
          ...prev,
          phone_number: selectedCountry.dial_code,
        }))
      }
    } else if (name === 'contract_type' || name === 'gender' || name === 'contract_duration') {
      setEmployee((prev) => ({
        ...prev,
        [name]: value,
        ...(name === 'contract_type' && value === 'FT'
          ? { hours_per_week: 40, hours_in_office: 40 }
          : {}),
      }))
    } else {
      setEmployee((prev) => ({ ...prev, [name]: value }))
    }
  }

  const fieldToErrorKey: Record<string, keyof typeof errors> = {
    first_name: 'firstName',
    last_name: 'lastName',
    work_email: 'workEmail',
    personal_email: 'personalEmail',
    date_of_birth: 'dateOfBirth',
    start_date: 'startDate',
    career_start_date: 'careerStartDate',
    country_of_work: 'countryOfWork',
    phone_number: 'phoneNumber',
    zip_code: 'zipCode',
    emergency_contact_email: 'emergencyContactEmail',
    emergency_contact_phone: 'emergencyContactNumber',
    salary: 'salary',
    temporary_contract_end_date: 'temporaryContractEndDate',
    probation_end_date: 'probationEndDate',
  }

  const getFieldError = (fieldKey: string) => {
    const errorKey = fieldToErrorKey[fieldKey]
    return errorKey ? errors[errorKey] : ''
  }

  const renderFieldError = (fieldKey: string) => {
    const message = getFieldError(fieldKey)
    if (!message) return null

    return (
      <Box data-field-error="true" textStyle="b-xs" color="error.100" w="100%" mt={1}>
        <Text>
          <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
            <WarningIcon />
          </Icon>
          {message}
        </Text>
      </Box>
    )
  }

  const isValid = () => {
    const newErrors = {
      startDate: '',
      firstName: '',
      lastName: '',
      careerStartDate: '',
      countryOfWork: '',
      personalEmail: '',
      emergencyContactEmail: '',
      workEmail: '',
      phoneNumber: '',
      emergencyContactNumber: '',
      zipCode: '',
      dateOfBirth: '',
      salary: '',
      temporaryContractEndDate: '',
      probationEndDate: '',
    }

    const requiredFieldKeys = Array.from(fieldConfigMap.values())
      .filter((field) => field.visible && field.required)
      .map((field) => field.key)
    const requiredFieldValues: Record<string, string | number | null | undefined> = {
      first_name: employee.first_name.trim(),
      last_name: employee.last_name.trim(),
      full_name: employee.full_name.trim(),
      local_name: employee.local_name.trim(),
      national_identification_number: employee.national_identification_number.trim(),
      work_email: employee.work_email.trim(),
      personal_email: employee.personal_email.trim(),
      education_level: employee.education_level,
      nationality: employee.nationality,
      country_of_residence: employee.country_of_residence,
      date_of_birth: employee.date_of_birth,
      start_date: employee.start_date,
      career_start_date: employee.career_start_date,
      country_of_work: employee.country_of_work,
      employee_number: employee.employee_number.trim(),
      department: employee.department,
      teams: employee.teams.length,
      current_position: employee.current_position,
      previous_position: employee.previous_position,
      contract_type: employee.contract_type,
      contract_duration: employee.contract_duration,
      phone_number: employee.phone_number,
      address: employee.address.trim(),
      city: employee.city.trim(),
      zip_code: employee.zip_code,
      gender: employee.gender,
      emergency_contact_email: employee.emergency_contact_email,
      emergency_contact_phone: employee.emergency_contact_phone,
      emergency_contact_type: employee.emergency_contact_type.trim(),
      emergency_contact_name: employee.emergency_contact_name.trim(),
      full_job_title: employee.full_job_title.trim(),
      salary: employee.salary,
      temporary_contract_end_date: employee.temporary_contract_end_date,
      probation_end_date: employee.probation_end_date,
      hours_per_week: employee.hours_per_week,
      number_of_holidays: employee.number_of_holidays,
      hours_in_office: employee.hours_in_office,
      hours_from_home: employee.hours_from_home,
      primary_evaluator: employee.primary_evaluator,
      secondary_evaluator: employee.secondary_evaluator,
      mentor: employee.mentor,
      last_day: employee.last_day,
    }
    const missingRequiredWithoutInlineError: string[] = []

    for (const fieldKey of requiredFieldKeys) {
      const value = requiredFieldValues[fieldKey]
      const isMissing =
        value === undefined ||
        value === null ||
        (typeof value === 'string' && isEmpty(value.trim())) ||
        (typeof value === 'number' && value <= 0)

      if (isMissing) {
        const errorKey = fieldToErrorKey[fieldKey]
        if (errorKey) {
          newErrors[errorKey] = t('validation.required', { field: getFieldLabel(fieldKey) })
        } else {
          missingRequiredWithoutInlineError.push(getFieldLabel(fieldKey))
        }
      }
    }

    if (employee.personal_email && !isEmail(employee.personal_email)) {
      newErrors.personalEmail = t('validation.emailInvalid')
    }
    if (employee.emergency_contact_email && !isEmail(employee.emergency_contact_email)) {
      newErrors.emergencyContactEmail = t('validation.emailInvalid')
    }
    if (employee.work_email && !isEmail(employee.work_email)) {
      newErrors.workEmail = t('validation.emailInvalid')
    }
    if (employee.phone_number && !isMobilePhone(employee.phone_number, 'any', { strictMode: true })) {
      newErrors.phoneNumber = t('validation.phoneInvalid')
    }
    if (
      employee.emergency_contact_phone &&
      !isMobilePhone(employee.emergency_contact_phone, 'any', { strictMode: true })
    ) {
      newErrors.emergencyContactNumber = t('validation.phoneInvalid')
    }
    if (employee.zip_code && !isPostalCode(employee.zip_code, 'any')) {
      newErrors.zipCode = t('validation.zipInvalid')
    }

    if (employee.date_of_birth && employee.start_date) {
      const dobDate = parseDateOnly(employee.date_of_birth)
      const startDateDate = parseDateOnly(employee.start_date)
      const currentYear = new Date().getFullYear()
      
      if (dobDate) {
        const dobYear = dobDate.getFullYear()
        
        if (dobDate > new Date()) {
          newErrors.dateOfBirth = t('validation.dateCannotBeInFuture', { field: getFieldLabel('date_of_birth') })
        }
        if (startDateDate && dobDate > startDateDate) {
          newErrors.dateOfBirth = t('validation.dateCannotBeAfter', {
            field: getFieldLabel('date_of_birth'),
            otherField: getFieldLabel('start_date'),
          })
        }
        if (dobYear < 1900 || dobYear > currentYear) {
          newErrors.dateOfBirth = t('validation.dateInvalid', { field: getFieldLabel('date_of_birth') })
        }
      }
      
      if (startDateDate) {
        const startYear = startDateDate.getFullYear()
        if (startYear < 1900 || startYear > currentYear + 10) {
          newErrors.startDate = t('validation.dateInvalid', { field: getFieldLabel('start_date') })
        }
      }
    }

    if (employee.career_start_date) {
      const careerDate = parseLocalDate(employee.career_start_date)
      if (careerDate > new Date()) {
        newErrors.careerStartDate = t('validation.dateCannotBeInFuture', { field: getFieldLabel('career_start_date') })
      }
    }

    setErrors(newErrors)
    const hasErrors = Object.values(newErrors).some((error) => error)
    if (missingRequiredWithoutInlineError.length > 0) {
      handleToast(
        toast,
        false,
        t('common.states.error'),
        t('validation.required', { field: missingRequiredWithoutInlineError[0] }),
      )
    }
    if (hasErrors) {
      setTimeout(() => {
        const firstErrorEl = document.querySelector('[data-field-error="true"]')
        firstErrorEl?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }
    return !hasErrors && missingRequiredWithoutInlineError.length === 0
  }

  const createEmployee = useMutation({
    mutationFn: addEmployee,
    onSuccess: async (newEmployee: EmployeeRecord) => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      queryClient.invalidateQueries({ queryKey: ['people'] })

      // Assign teams if selected
      if (employee.teams.length > 0) {
        updateEmployeeMutation.mutate({
          id: newEmployee.id,
          teams: employee.teams,
        })
      }

      // Upload documents for the new employee
      let docFailures = 0
      for (const doc of uploadedDocs) {
        try {
          await createDocument({
            title: doc.title,
            version: '1.0',
            file: doc.file,
            recipient: String(newEmployee.id),
            status: doc.status,
          })
        } catch {
          docFailures++
        }
      }

      // Assign checked templates
      let templateFailures = 0
      for (const templateId of checkedTemplates) {
        const tmpl = templatesList.find((t: TemplateRecord) => String(t.id) === templateId)
        if (!tmpl) continue
        try {
          const status = templateStatusOverrides[templateId] || tmpl.default_status || 'pending_digital_signature'
          await createDocumentFromTemplate({
            template: templateId,
            title: tmpl.title,
            signing_required: status === 'pending_signing',
            digital_signing_required: status === 'pending_digital_signature',
            recipient_type: 'user',
            recipients: [String(newEmployee.id)],
          })
        } catch {
          templateFailures++
        }
      }

      let pathFailures = 0
      for (const path of selectedPathList) {
        try {
          await createPathParticipant(String(path.id), {
            employee: String(newEmployee.id),
            path: String(path.id),
          })
        } catch {
          pathFailures++
        }
      }

      if (docFailures > 0 || templateFailures > 0 || pathFailures > 0) {
        handleToast(toast, false, t('common.states.warning'), t('workforce.people.messages.someAttachmentsFailed'))
      }

      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['paths'] })

      handleToast(
        toast,
        true,
        t('workforce.people.messages.employeeAddedTitle'),
        t('workforce.people.messages.employeeAddedMessage', { name: newEmployee.full_name }),
      )

      if (onAddEmployee) {
        onAddEmployee(newEmployee)
      }
      if (onSuccess) {
        onSuccess(newEmployee)
      }

      onClose()
    },
    onError: (error: Error & { response?: { data?: Record<string, unknown> } }) => {
      let errorMsg = t('workforce.people.messages.addEmployeeFailedMessage')

      // Readable labels for backend field names
      const fieldLabels: Record<string, string> = {
        first_name: t('common.fields.firstName'),
        last_name: t('common.fields.lastName'),
        work_email: t('workforce.people.fields.workEmail'),
        personal_email: t('workforce.people.fields.personalEmail'),
        date_of_birth: t('workforce.people.fields.dateOfBirth'),
        start_date: t('workforce.people.fields.startDate'),
        career_start_date: t('workforce.people.fields.careerStartDate'),
        country_of_work: t('workforce.people.countryOfWork'),
        phone_number: t('common.form.phone'),
        salary: t('workforce.people.fields.grossSalary'),
        temporary_contract_end_date: t('workforce.people.fields.temporaryContractEndDate'),
        probation_end_date: t('workforce.people.fields.probationPeriodEndDate'),
        hours_per_week: t('workforce.mutations.fields.workHoursPerWeek'),
      }

      if (error?.response?.data) {
        const data = error.response.data
        if (typeof data === 'object' && data !== null) {
          // Build error messages with field names and map to form errors
          const messages: string[] = []
          const newErrors: Record<string, string> = {}

          for (const [field, value] of Object.entries(data)) {
            const msg = Array.isArray(value) ? String(value[0]) : String(value)
            const label = fieldLabels[field] || field.replace(/_/g, ' ')
            messages.push(`${label}: ${msg}`)

            const errorKey = fieldToErrorKey[field]
            if (errorKey) {
              newErrors[errorKey] = `${label}: ${msg}`
            }
          }

          if (Object.keys(newErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...newErrors }))
            // Scroll to first error field after state update
            setTimeout(() => {
              const firstErrorEl = document.querySelector('[data-field-error="true"]')
              firstErrorEl?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }, 100)
          }

          if (messages.length > 0) {
            errorMsg = messages.join('\n')
          }
        }
      }
      handleToast(toast, false, t('workforce.people.messages.addEmployeeFailedTitle'), errorMsg)
    },

  })

  const updateEmployeeMutation = useMutation({
    mutationFn: updateEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      queryClient.invalidateQueries({ queryKey: ['people'] })
    },
  })

  const handleSubmit = () => {
    if (isValid()) {
      const countryCode = getCodeByCountryName(employee.country_of_residence)
      const nationalityCode = getCodeByCountryName(employee.nationality)
      const countryOfWorkCode = getCodeByCountryName(employee.country_of_work)

      // Create employee without teams first
      const employeePayload = {
        ...employee,
        country_of_residence: countryCode || employee.country_of_residence || null,
        nationality: nationalityCode || employee.nationality,
        country_of_work: countryOfWorkCode || employee.country_of_work,
        // Remove teams from creation payload
      }

      // to allow creation of employees without contract type, duration, gender
      if (!employeePayload.contract_type) employeePayload.contract_type = null
      if (!employeePayload.contract_duration) employeePayload.contract_duration = null
      if (!employeePayload.gender) employeePayload.gender = null

      // Send null instead of empty string for non-temporary contract date fields
      if (employeePayload.contract_duration !== 'TE') {
        if (!employeePayload.temporary_contract_end_date) employeePayload.temporary_contract_end_date = null
        if (!employeePayload.probation_end_date) employeePayload.probation_end_date = null
      }

      if (dob) {
        const formattedDOB = `${dob.getFullYear()}-${String(dob.getMonth() + 1).padStart(2, '0')}-${String(dob.getDate()).padStart(2, '0')}`
        employeePayload.date_of_birth = formattedDOB
      } else {
        delete employeePayload.date_of_birth
      }

      if (startDate) {
        const formattedStart = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(
          2,
          '0',
        )}-${String(startDate.getDate()).padStart(2, '0')}`
        employeePayload.start_date = formattedStart
      }

      if (!employee.career_start_date) {
        delete employeePayload.career_start_date
      }

      if (!employee.last_day) {
        delete employeePayload.last_day
      }

      createEmployee.mutate(employeePayload)
    }
  }

  const handleClose = () => {
    setEmergencyPhoneCountry('')
    setEmergencyPhoneCode('')
    setAvatarPreview(null)
    setUploadedDocs([])
    setCheckedTemplates(new Set())
    setTemplateStatusOverrides({})
    setPathSearchTerm('')
    setPathSearchResetKey((prev) => prev + 1)
    setSelectedPaths({})
    setEmployee({
      first_name: '',
      last_name: '',
      full_name: '',
      local_name: '',
      city: '',
      teams: [],
      nationality: '',
      country_of_residence: '',
      national_identification_number: '',
      country_of_work: '',
      phone_country: '',
      personal_email: '',
      education_level: '',
      manager: '',
      career_start_date: '',
      work_email: '',
      employee_number: '',
      hours_per_week: 0,
      start_date: '',
      contract_type: '',
      contract_duration: '',
      number_of_holidays: 0,
      current_position: '',
      previous_position: '',
      department: 'OP',
      hours_in_office: 0,
      hours_from_home: 0,
      zip_code: '',
      gender: '',
      phone_number: '',
      address: '',
      date_of_birth: '',
      job_profiles: [],
      profile_picture: null,
      is_admin: false,
      emergency_contact_email: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_type: '',
      emergency_phone_country: '',
      primary_evaluator: '',
      secondary_evaluator: '',
      last_day: '',
      mentor: '',
      full_job_title: '',
      salary: 0,
      probation_end_date: '',
      temporary_contract_end_date: '',
    })
    setErrors({
      firstName: '',
      lastName: '',
      workEmail: '',
      dateOfBirth: '',
      startDate: '',
      personalEmail: '',
      zipCode: '',
      careerStartDate: '',
      phoneNumber: '',
      emergencyContactNumber: '',
      emergencyContactEmail: '',
      countryOfWork: '',
      salary: '',
      temporaryContractEndDate: '',
      probationEndDate: '',
    })
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="3xl" closeOnOverlayClick={false} isCentered>
      <ModalOverlay />
      <ModalContent h="90vh" w="900px" overflow="auto">
        <ModalHeader height="48px" p="12px 36px 12px 16px" {...modal.header}>
          {t('workforce.people.addNewPeople')}
        </ModalHeader>
        <ModalCloseButton sx={modal.closeButton} />

        <ModalBody>
          <Flex alignItems="center" mt={3} mb={4} width="560px">
            <Avatar src={avatarPreview || ''} />
            <Box ml={3}>
              <Text fontWeight="bold">{t('workforce.people.profilePicture.title')}</Text>
              <Text fontSize="sm" color="gray.500">
                {t('workforce.people.profilePicture.hint')}
              </Text>
            </Box>
            <Button ml="auto" mr={1} variant="formUtility" onClick={() => fileInputRef.current?.click()}>
              <Icon boxSize="14px" fill="none" color="solid.500">
                <UploadCloud02Icon />
              </Icon>
              {t('workforce.people.profilePicture.browseFile')}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png, image/jpeg"
                style={{ display: 'none' }}
                onChange={() => {
                  setEmployee((prev) => ({
                    ...prev,
                    profile_picture: fileInputRef.current?.files?.[0] || null,
                  }))
                  setAvatarPreview(
                    fileInputRef.current?.files?.[0] ? URL.createObjectURL(fileInputRef.current.files[0]) : null,
                  )
                }}
              />
            </Button>
          </Flex>
          <CollapsibleSection
            title={t('workforce.people.sections.personalInformation')}
            defaultOpen={sectionsOpen.personal}
            badge={errors.firstName || errors.lastName || errors.workEmail ? '!' : undefined}
          >
          <SimpleGrid columns={2} spacing={4}>
            {isFieldVisible('first_name') && (
              <Box width="100%">
                <ModalInput label={t('common.fields.firstName')} placeholder={examplePlaceholder('John')} type="text" name="first_name" value={employee.first_name} onChange={handleChange} required={isFieldRequired('first_name')} />
                {renderFieldError('first_name')}
              </Box>
            )}
            {isFieldVisible('last_name') && (
              <Box width="100%">
                <ModalInput label={t('common.fields.lastName')} placeholder={examplePlaceholder('Doe')} type="text" name="last_name" value={employee.last_name} onChange={handleChange} required={isFieldRequired('last_name')} />
                {renderFieldError('last_name')}
              </Box>
            )}
            {isFieldVisible('full_name') && (
              <Box width="100%">
                <ModalInput label={t('workforce.people.fields.fullName')} placeholder={examplePlaceholder('John Duchac Doe')} type="text" name="full_name" value={employee.full_name} onChange={handleChange} required={isFieldRequired('full_name')} />
              </Box>
            )}
            {isFieldVisible('local_name') && (
              <Box width="100%">
                <ModalInput label={t('workforce.people.fields.localName')} placeholder={examplePlaceholder('董晨琛')} type="text" name="local_name" value={employee.local_name} onChange={handleChange} required={isFieldRequired('local_name')} />
              </Box>
            )}
            {isFieldVisible('national_identification_number') && (
              <Box width="100%">
                <ModalInput label={t('workforce.people.fields.nationalIdNumber')} placeholder={examplePlaceholder('123ABC')} type="text" name="national_identification_number" value={employee.national_identification_number} onChange={handleChange} required={isFieldRequired('national_identification_number')} />
              </Box>
            )}
            {isFieldVisible('personal_email') && (
              <Box width="100%">
                <ModalInput label={t('workforce.people.fields.personalEmail')} placeholder={examplePlaceholder('johndoe@gmail.com')} type="email" name="personal_email" value={employee.personal_email} onChange={handleChange} required={isFieldRequired('personal_email')} />
                {renderFieldError('personal_email')}
              </Box>
            )}
            {isFieldVisible('education_level') && (
              <Box width="100%" position="relative" zIndex={2000}>
                <ModalSelect
                  label={t('workforce.people.fields.educationLevel')}
                  name="education_level"
                  value={employee.education_level}
                  options={[
                    { value: '', label: t('workforce.people.educationLevels.selectPrompt') },
                    { value: 'CSE', label: t('workforce.people.educationLevels.completedSecondary') },
                    { value: 'CPSE', label: t('workforce.people.educationLevels.completedPostSecondaryVocational') },
                    { value: 'SUE', label: t('workforce.people.educationLevels.someUniversityNoDegree') },
                    { value: 'ADE', label: t('workforce.people.educationLevels.associateDegree') },
                    { value: 'BDE', label: t('workforce.people.educationLevels.bachelorDegree') },
                    { value: 'SGE', label: t('workforce.people.educationLevels.someGraduateNoDegree') },
                    { value: 'MD', label: t('workforce.people.educationLevels.masterDegree') },
                    { value: 'PHD', label: t('workforce.people.educationLevels.phd') },
                    { value: 'Other', label: t('workforce.people.educationLevels.other') },
                  ]}
                  onChange={handleChange}
                  required={isFieldRequired('education_level')}
                />
              </Box>
            )}
            {isFieldVisible('nationality') && (
              <Box width="100%">
                <ModalSelect
                  label={t('workforce.people.fields.nationality')}
                  name="nationality"
                  value={employee.nationality}
                  options={countrySelectOptions}
                  onChange={(e) => setEmployee((prev) => ({ ...prev, nationality: e.target.value }))}
                  showCountryFlag
                  required={isFieldRequired('nationality')}
                />
              </Box>
            )}
            {isFieldVisible('country_of_residence') && (
              <Box width="100%">
                <ModalSelect
                  label={t('workforce.people.fields.countryOfResidence')}
                  name="country_of_residence"
                  value={employee.country_of_residence}
                  options={countrySelectOptions}
                  onChange={(e) => setEmployee((prev) => ({ ...prev, country_of_residence: e.target.value }))}
                  showCountryFlag
                  required={isFieldRequired('country_of_residence')}
                />
              </Box>
            )}
            {isFieldVisible('phone_number') && (
              <Box width="100%">
                <ModalPhoneInput
                  label={t('workforce.people.fields.phoneNumber')}
                  value={employee.phone_number}
                  onChange={(phone) => {
                    const matched = phoneNumbers.find((c) => phone.startsWith(c.dial_code))
                    setEmployee((prev) => ({
                      ...prev,
                      phone_number: phone,
                      phone_country: matched ? matched.code.toLowerCase() : prev.phone_country,
                    }))
                    if (matched) setPhoneCode(matched.dial_code)
                  }}
                  defaultCountry={employee.phone_country || 'nl'}
                  error={getFieldError('phone_number')}
                  required={isFieldRequired('phone_number')}
                />
              </Box>
            )}
            {isFieldVisible('address') && (
              <Box width="100%">
                <ModalInput label={t('workforce.people.fields.address')} placeholder={examplePlaceholder('123 Avenue')} type="text" name="address" value={employee.address} onChange={handleChange} required={isFieldRequired('address')} />
              </Box>
            )}
            {isFieldVisible('city') && (
              <Box width="100%">
                <ModalInput label={t('workforce.people.fields.city')} placeholder={examplePlaceholder('Amsterdam')} type="text" name="city" value={employee.city} onChange={handleChange} required={isFieldRequired('city')} />
              </Box>
            )}
            {isFieldVisible('zip_code') && (
              <Box width="100%">
                <ModalInput label={t('workforce.people.fields.zipCode')} placeholder={examplePlaceholder('1234 AB')} type="text" name="zip_code" value={employee.zip_code} onChange={handleChange} required={isFieldRequired('zip_code')} />
                {renderFieldError('zip_code')}
              </Box>
            )}
            {isFieldVisible('date_of_birth') && (
              <Box width="100%">
                <Text textStyle="l-xs" color={isFieldRequired('date_of_birth') ? 'solid.700' : 'solid.500'} fontWeight={isFieldRequired('date_of_birth') ? '600' : undefined} mb={1}>
                  {t('workforce.people.fields.dateOfBirth')}
                  {isFieldRequired('date_of_birth') && <Text as="span" color="primary.100"> *</Text>}
                </Text>
                <CustomDateInput
                  required={isFieldRequired('date_of_birth')}
                  placeholder={t('common.placeholders.pickADate')}
                  defaultValue={dob}
                  onChange={(selectedDate) => {
                    setDob(selectedDate)
                    setEmployee((prev) => ({
                      ...prev,
                      date_of_birth: selectedDate
                        ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
                        : '',
                    }))
                  }}
                />
                {renderFieldError('date_of_birth')}
              </Box>
            )}
            {isFieldVisible('gender') && (
              <ModalSelect
                label={t('workforce.people.fields.gender')}
                name="gender"
                value={employee.gender || ''}
                options={[
                  { value: 'M', label: t('workforce.people.gender.man') },
                  { value: 'F', label: t('workforce.people.gender.woman') },
                  { value: 'X', label: t('workforce.people.gender.x') },
                ]}
                onChange={(e) => setEmployee((prev) => ({ ...prev, gender: e.target.value }))}
                searchDisabled={true}
                required={isFieldRequired('gender')}
              />
            )}
            {isFieldVisible('emergency_contact_email') && (
              <Box width="100%">
                <ModalInput label={t('workforce.people.fields.emergencyContactEmail')} placeholder={examplePlaceholder('johndoe@gmail.com')} type="email" name="emergency_contact_email" value={employee.emergency_contact_email} onChange={handleChange} required={isFieldRequired('emergency_contact_email')} />
                {renderFieldError('emergency_contact_email')}
              </Box>
            )}
            {isFieldVisible('emergency_contact_phone') && (
              <Box width="100%">
                <ModalPhoneInput
                  label={t('workforce.people.fields.emergencyContactNumber')}
                  value={employee.emergency_contact_phone}
                  onChange={(phone) => {
                    const matched = phoneNumbers.find((c) => phone.startsWith(c.dial_code))
                    setEmployee((prev) => ({
                      ...prev,
                      emergency_contact_phone: phone,
                      emergency_phone_country: matched ? matched.code.toLowerCase() : prev.emergency_phone_country,
                    }))
                    if (matched) setEmergencyPhoneCode(matched.dial_code)
                  }}
                  defaultCountry={emergencyPhoneCountry || 'nl'}
                  error={getFieldError('emergency_contact_phone')}
                  required={isFieldRequired('emergency_contact_phone')}
                />
              </Box>
            )}
            {isFieldVisible('emergency_contact_type') && (
              <Box width="100%">
                <ModalInput label={t('workforce.people.fields.emergencyContactType')} placeholder={examplePlaceholder('Parent, Partner, Child, etc.')} type="text" name="emergency_contact_type" value={employee.emergency_contact_type} onChange={handleChange} required={isFieldRequired('emergency_contact_type')} />
              </Box>
            )}
            {isFieldVisible('emergency_contact_name') && (
              <Box width="100%">
                <ModalInput label={t('workforce.people.fields.emergencyContactName')} placeholder={examplePlaceholder('John Doe')} type="text" name="emergency_contact_name" value={employee.emergency_contact_name} onChange={handleChange} required={isFieldRequired('emergency_contact_name')} />
              </Box>
            )}
          </SimpleGrid>
          </CollapsibleSection>

          <CollapsibleSection
            title={t('workforce.people.sections.workInformation')}
            defaultOpen={sectionsOpen.work}
            badge={errors.workEmail || errors.countryOfWork || errors.startDate || errors.salary || errors.temporaryContractEndDate || errors.probationEndDate ? '!' : undefined}
          >
          <SimpleGrid columns={2} spacing={4}>
            {isFieldVisible('work_email') && (
              <Box>
                <ModalInput label={t('workforce.people.fields.workEmail')} required={isFieldRequired('work_email')} type="text" name="work_email" placeholder={examplePlaceholder('john@work.nl')} value={employee.work_email} onChange={handleChange} />
                {renderFieldError('work_email')}
              </Box>
            )}
            {isFieldVisible('full_job_title') && (
              <Tooltip label={t('workforce.people.fields.fullJobTitleSignature')} placement="top" hasArrow>
                <Box><ModalInput label={t('workforce.people.fields.fullJobTitleShort')} name="full_job_title" type="text" placeholder={examplePlaceholder('Software Engineer')} value={employee.full_job_title} onChange={handleChange} required={isFieldRequired('full_job_title')} /></Box>
              </Tooltip>
            )}

            {isFieldVisible('employee_number') && (
              <ModalInput label={t('workforce.people.fields.employeeNumber')} name="employee_number" type="text" placeholder={examplePlaceholder('RC1234XXX')} value={employee.employee_number} onChange={handleChange} required={isFieldRequired('employee_number')} />
            )}
            {isFieldVisible('department') && (
              <ModalSelect label={t('workforce.people.fields.department')} name="department" searchDisabled={true} required={isFieldRequired('department')} value={employee.department} options={[{ value: 'OP', label: t('workforce.people.supportType.operations') }, { value: 'BO', label: t('workforce.people.supportType.backOffice') }]} onChange={handleChange} />
            )}

            {isFieldVisible('country_of_work') && (
              <Box>
                <ModalSelect label={t('workforce.people.countryOfWork')} required={isFieldRequired('country_of_work')} name="country_of_work" value={employee.country_of_work} options={countrySelectOptions} onChange={(e) => { setEmployee((prev) => ({ ...prev, country_of_work: e.target.value })); setErrors({ ...errors, countryOfWork: '' }) }} showCountryFlag />
                {renderFieldError('country_of_work')}
              </Box>
            )}
            {isFieldVisible('teams') && (
              <ModalMultiSelect label={t('workforce.teams.teamName')} selectedValues={employee.teams.map((teamId) => { const team = teams.find((teamItem) => teamItem.id === teamId); return { key: teamId, name: team?.name || '' } })} options={teams.map((team) => ({ key: team.id, name: team.name }))} onChange={(selectedList) => { setEmployee((prev) => ({ ...prev, teams: selectedList.map((item) => item.key) })) }} onSearchChange={(setSearchTerm) => setTeamsSearch(setSearchTerm)} isTop />
            )}

            {isFieldVisible('current_position') && (
              <ModalSelect label={t('workforce.people.fields.currentPosition')} name="current_position" value={employee.current_position} required={isFieldRequired('current_position')} options={[{ value: '', label: t('workforce.people.placeholders.selectCurrentPosition') }, ...(jobPositions ?? []).map((job) => ({ value: job.id, label: job.title }))]} onChange={handleChange} />
            )}
            {isFieldVisible('previous_position') && (
              <ModalSelect label={t('workforce.people.fields.previousPosition')} name="previous_position" value={employee.previous_position} required={isFieldRequired('previous_position')} options={[{ value: '', label: t('workforce.people.placeholders.selectPreviousPosition') }, ...jobPositions.map((job) => ({ value: job.id, label: job.title }))]} onChange={handleChange} />
            )}

            {isFieldVisible('contract_type') && (
              <ModalSelect label={t('workforce.mutations.fields.contractType')} name="contract_type" value={employee.contract_type || ''} searchDisabled={true} required={isFieldRequired('contract_type')} options={[{ value: 'FT', label: t('workforce.mutations.contractTypes.fullTime') }, { value: 'PT', label: t('workforce.mutations.contractTypes.partTime') }, { value: 'IN', label: t('workforce.mutations.contractTypes.internship') }, { value: 'CN', label: t('workforce.people.contractTypes.contractor') }, { value: 'FL', label: t('workforce.mutations.contractTypes.freelance') }]} onChange={handleChange} />
            )}
            {isFieldVisible('contract_duration') && (
              <ModalSelect label={t('workforce.mutations.fields.contractDuration')} name="contract_duration" value={employee.contract_duration || ''} searchDisabled={true} required={isFieldRequired('contract_duration')} options={[{ value: 'TE', label: t('workforce.people.contractDuration.temporary') }, { value: 'PM', label: t('workforce.people.contractDuration.permanent') }]} onChange={handleChange} />
            )}

            {isFieldVisible('start_date') && (
              <Box>
                <Text textStyle="l-xs" color={isFieldRequired('start_date') ? 'solid.700' : 'solid.500'} fontWeight={isFieldRequired('start_date') ? '600' : undefined} mb={1}>{t('workforce.people.fields.startDate')}{isFieldRequired('start_date') && <Text as="span" color="primary.100"> *</Text>}</Text>
                <CustomDateInput required={isFieldRequired('start_date')} placeholder={t('common.placeholders.pickADate')} defaultValue={startDate} onChange={(selectedDate) => { setStartDate(selectedDate); setEmployee((prev) => ({ ...prev, start_date: selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : '' })) }} />
                {renderFieldError('start_date')}
              </Box>
            )}
            {isFieldVisible('salary') && (
              <Box>
                <ModalInput label={t('workforce.people.fields.grossSalary')} placeholder={examplePlaceholder('3500.00')} type="number" name="salary" value={employee.salary} onChange={handleChange} required={isFieldRequired('salary')} />
                {renderFieldError('salary')}
              </Box>
            )}

            {isFieldVisible('probation_end_date') && (
              <Box>
                <Text textStyle="l-xs" color={isFieldRequired('probation_end_date') ? 'solid.700' : 'solid.500'} fontWeight={isFieldRequired('probation_end_date') ? '600' : undefined} mb={1}>{t('workforce.people.fields.probationPeriodEndDate')}{isFieldRequired('probation_end_date') && <Text as="span" color="primary.100"> *</Text>}</Text>
                <CustomDateInput required={isFieldRequired('probation_end_date')} placeholder={t('common.placeholders.pickADate')} onChange={(selectedDate) => { setEmployee((prev) => ({ ...prev, probation_end_date: selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : '' })) }} />
                {renderFieldError('probation_end_date')}
              </Box>
            )}
            {isFieldVisible('temporary_contract_end_date') && (
              <Box>
                <Text textStyle="l-xs" color={isFieldRequired('temporary_contract_end_date') ? 'solid.700' : 'solid.500'} fontWeight={isFieldRequired('temporary_contract_end_date') ? '600' : undefined} mb={1}>{t('workforce.people.fields.temporaryContractEndDate')}{isFieldRequired('temporary_contract_end_date') && <Text as="span" color="primary.100"> *</Text>}</Text>
                <CustomDateInput required={isFieldRequired('temporary_contract_end_date')} placeholder={t('common.placeholders.pickADate')} onChange={(selectedDate) => { setEmployee((prev) => ({ ...prev, temporary_contract_end_date: selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : '' })) }} />
                {renderFieldError('temporary_contract_end_date')}
              </Box>
            )}

            {isFieldVisible('hours_per_week') && (
              <CustomNumericInput label={t('workforce.mutations.fields.workHoursPerWeek')} value={employee.hours_per_week} onChange={(value: number) => setEmployee((prev) => ({ ...prev, hours_per_week: value }))} min={0} max={100} step={1} format={(num) => `${num} ${t('common.units.hour', { count: num })}`} required={isFieldRequired('hours_per_week')} />
            )}
            {isFieldVisible('number_of_holidays') && (
              <CustomNumericInput label={t('workforce.people.fields.holidaysPerYear')} value={employee.number_of_holidays} onChange={(value: number) => setEmployee((prev) => ({ ...prev, number_of_holidays: value }))} min={0} max={100} step={1} format={(num) => `${num} ${t('common.units.day', { count: num })}`} required={isFieldRequired('number_of_holidays')} />
            )}

            {isFieldVisible('hours_in_office') && (
              <CustomNumericInput label={t('workforce.mutations.fields.workHoursInOffice')} value={employee.hours_in_office} onChange={(value: number) => setEmployee((prev) => ({ ...prev, hours_in_office: value }))} min={0} max={100} step={1} format={(num) => `${num} ${t('common.units.hour', { count: num })}`} required={isFieldRequired('hours_in_office')} />
            )}
            {isFieldVisible('hours_from_home') && (
              <CustomNumericInput label={t('workforce.mutations.fields.workHoursFromHome')} value={employee.hours_from_home} onChange={(value: number) => setEmployee((prev) => ({ ...prev, hours_from_home: value }))} min={0} max={100} step={1} format={(num) => `${num} ${t('common.units.hour', { count: num })}`} required={isFieldRequired('hours_from_home')} />
            )}

            {isFieldVisible('primary_evaluator') && (
              <ModalSelect label={t('workforce.people.fields.primaryEvaluator')} name="primary_evaluator" value={employee.primary_evaluator} required={isFieldRequired('primary_evaluator')} options={optionsPrimary} onChange={(e) => { const managerId = e.target.value; const managerObj = managersQuery.data?.data?.find((m: ManagerRecord) => m.id === managerId) || null; setSelectedPrimaryManager(managerObj); setEmployee((prev) => ({ ...prev, primary_evaluator: managerId })); setManagerSearch('') }} hasProfilePicture={true} isPaginated={false} onSearch={(term) => setManagerSearch(term)} isLoading={managersQuery.isLoading} placeholder={t('common.labels.searchPlaceholder')} isTop={true} emptyPrompt={t('common.labels.searchPlaceholder')} />
            )}
            {isFieldVisible('secondary_evaluator') && (
              <ModalSelect label={t('workforce.people.fields.secondaryEvaluator')} name="secondary_evaluator" value={employee.secondary_evaluator} required={isFieldRequired('secondary_evaluator')} options={optionsSecondary} onChange={(e) => { const managerId = e.target.value; const managerObj = managersQuery.data?.data?.find((m: ManagerRecord) => m.id === managerId) || null; setSelectedSecondaryManager(managerObj); setEmployee((prev) => ({ ...prev, secondary_evaluator: managerId })); setManagerSearch('') }} hasProfilePicture={true} isPaginated={false} onSearch={(term) => setManagerSearch(term)} isLoading={managersQuery.isLoading} placeholder={t('common.labels.searchPlaceholder')} isTop={true} emptyPrompt={t('common.labels.searchPlaceholder')} />
            )}

            {isFieldVisible('mentor') && (
              <ModalSelect label={t('workforce.people.fields.mentor')} name="mentor" value={employee.mentor} required={isFieldRequired('mentor')} options={optionsMentor} onChange={(e) => { const mentorId = e.target.value; const managerObj = managersQuery.data?.data?.find((m: ManagerRecord) => m.id === mentorId) || null; setSelectedMentorManager(managerObj); setEmployee((prev) => ({ ...prev, mentor: mentorId })); setManagerSearch('') }} hasProfilePicture={true} isPaginated={false} onSearch={(term) => setManagerSearch(term)} isLoading={managersQuery.isLoading} placeholder={t('common.labels.searchPlaceholder')} isTop={true} emptyPrompt={t('common.labels.searchPlaceholder')} />
            )}
            {isFieldVisible('career_start_date') && (
              <Box>
                <Text textStyle="l-xs" color={isFieldRequired('career_start_date') ? 'solid.700' : 'solid.500'} fontWeight={isFieldRequired('career_start_date') ? '600' : undefined} mb={1}>{t('workforce.people.fields.careerStartDate')}{isFieldRequired('career_start_date') && <Text as="span" color="primary.100"> *</Text>}</Text>
                <CustomDateInput required={isFieldRequired('career_start_date')} placeholder={t('common.placeholders.pickADate')} onChange={(selectedDate) => { setEmployee((prev) => ({ ...prev, career_start_date: selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : '' })) }} />
                {renderFieldError('career_start_date')}
              </Box>
            )}

            {isFieldVisible('last_day') && (
              <Box>
                <Text textStyle="l-xs" color={isFieldRequired('last_day') ? 'solid.700' : 'solid.500'} fontWeight={isFieldRequired('last_day') ? '600' : undefined} mb={1}>{t('workforce.people.deactivation.lastDayLabel')}{isFieldRequired('last_day') && <Text as="span" color="primary.100"> *</Text>}</Text>
                <CustomDateInput required={isFieldRequired('last_day')} placeholder={t('common.placeholders.pickADate')} onChange={(selectedDate) => { setLastDay(selectedDate); setEmployee((prev) => ({ ...prev, last_day: selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : '' })) }} />
              </Box>
            )}
          </SimpleGrid>
          </CollapsibleSection>

          <CollapsibleSection
            title={t('workforce.people.sections.documents') || 'Documents'}
            defaultOpen={sectionsOpen.documents}
            badge={uploadedDocs.length > 0 || checkedTemplates.size > 0
              ? String(uploadedDocs.length + checkedTemplates.size)
              : undefined}
          >
            <VStack spacing="16px" align="stretch">
              {/* Upload documents */}
              <Box>
                <Text fontSize="12px" fontWeight="600" color="solid.500" mb="8px">
                  {t('workforce.people.documents.uploadTitle') || 'Upload Documents'}
                </Text>
                <Text fontSize="11px" color="solid.400" mb="12px">
                  {t('workforce.people.documents.uploadHint') || 'Upload PDF documents for the new employee to sign.'}
                </Text>

                {uploadedDocs.map((doc, idx) => (
                  <Box key={idx} mb="10px" p="10px" borderRadius="8px" border="1px solid" borderColor="solid.100" bg="solid.25">
                    <Flex align="center" gap="10px">
                      <Text fontSize="11px" color="solid.400" isTruncated flex="1">{doc.file.name}</Text>
                      <Button
                        size="xs"
                        variant="ghost"
                        color="error.100"
                        onClick={() => setUploadedDocs((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        {t('common.actions.remove') || 'Remove'}
                      </Button>
                    </Flex>
                    <Flex align="center" gap="10px" mt="8px">
                      <Box flex="1">
                        <Input
                          size="sm"
                          fontSize="12px"
                          borderRadius="6px"
                          placeholder={t('common.fields.title') || 'Title'}
                          value={doc.title}
                          onChange={(e) => {
                            const updated = [...uploadedDocs]
                            updated[idx] = { ...doc, title: e.target.value }
                            setUploadedDocs(updated)
                          }}
                        />
                      </Box>
                      <Select
                        size="xs"
                        fontSize="11px"
                        borderRadius="6px"
                        w="auto"
                        minW="130px"
                        value={doc.status}
                        onChange={(e) => {
                          const updated = [...uploadedDocs]
                          updated[idx] = { ...doc, status: e.target.value }
                          setUploadedDocs(updated)
                        }}
                      >
                        <option value="no_signing">{t('documents.statuses.noSigning')}</option>
                        <option value="pending_signing">{t('documents.statuses.pendingSigning')}</option>
                        <option value="pending_digital_signature">{t('documents.statuses.pendingDigitalSignature')}</option>
                      </Select>
                    </Flex>
                  </Box>
                ))}

                <Button
                  variant="formUtility"
                  size="sm"
                  onClick={() => {
                    const input = document.createElement('input')
                    input.type = 'file'
                    input.accept = 'application/pdf'
                    input.multiple = true
                    input.onchange = () => {
                      if (input.files) {
                        const newDocs = Array.from(input.files).map((file) => ({
                          file,
                          title: file.name.replace(/\.pdf$/i, ''),
                          status: 'no_signing',
                        }))
                        setUploadedDocs((prev) => [...prev, ...newDocs])
                      }
                    }
                    input.click()
                  }}
                >
                  <Icon boxSize="14px" fill="none" color="solid.500" mr="6px">
                    <UploadCloud02Icon />
                  </Icon>
                  {t('workforce.people.documents.addDocument') || 'Add Document'}
                </Button>
              </Box>

              {/* Org-wide templates checklist */}
              {templatesList.length > 0 && (
                <Box>
                  <Text fontSize="12px" fontWeight="600" color="solid.500" mb="8px">
                    {t('workforce.people.documents.orgTemplatesTitle') || 'Organization Templates'}
                  </Text>
                  <Text fontSize="11px" color="solid.400" mb="12px">
                    {t('workforce.people.documents.orgTemplatesHint') || 'These templates will be assigned to the new employee. Uncheck any you want to skip.'}
                  </Text>
                  <Box
                    borderRadius="12px"
                    bg="white"
                  >
                  <VStack spacing="8px" align="stretch">
                    {templatesList.map((tmpl: TemplateRecord) => {
                      const id = String(tmpl.id)
                      const checked = checkedTemplates.has(id)
                      const currentStatus = templateStatusOverrides[id] || tmpl.default_status || 'pending_digital_signature'
                      return (
                        <Flex
                          key={id}
                          align="flex-start"
                          gap="12px"
                          p="12px"
                          borderRadius="12px"
                          border="1px solid"
                          borderColor={checked ? 'primary.100' : 'solid.100'}
                          bg={checked ? 'primary.0' : 'white'}
                          transition="all 0.15s"
                          _hover={{
                            bg: checked ? 'primary.0' : 'solid.25',
                            borderColor: checked ? 'primary.100' : 'solid.200',
                          }}
                          cursor="pointer"
                          onClick={() => {
                            setCheckedTemplates((prev) => {
                              const next = new Set(prev)
                              if (next.has(id)) next.delete(id)
                              else next.add(id)
                              return next
                            })
                          }}
                        >
                          <Checkbox
                            mt="2px"
                            isChecked={checked}
                            colorScheme="purple"
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => {
                              setCheckedTemplates((prev) => {
                                const next = new Set(prev)
                                if (next.has(id)) next.delete(id)
                                else next.add(id)
                                return next
                              })
                            }}
                          />
                          <Box flex="1" minW={0}>
                            <Flex align="center" justify="space-between" gap="10px" mb={tmpl.version ? '4px' : '0'}>
                              <Text fontSize="13px" fontWeight="600" color="solid.900" noOfLines={1}>
                                {tmpl.title}
                              </Text>
                            </Flex>
                            {tmpl.version && (
                              <Text fontSize="11px" color="solid.500" noOfLines={1}>
                                {t('common.fields.version') || 'Version'}: {tmpl.version}
                              </Text>
                            )}
                          </Box>
                          {checked && (
                            <Box w="160px" flexShrink={0} bg="white" borderRadius="6px" onClick={(e) => e.stopPropagation()}>
                              <ModalSelect
                                label=""
                                name={`template-status-${id}`}
                                value={currentStatus}
                                w="160px"
                                h="34px"
                                onChange={(e) => {
                                  setTemplateStatusOverrides((prev) => ({ ...prev, [id]: e.target.value }))
                                }}
                                options={[
                                  { value: 'pending_digital_signature', label: t('documents.statuses.pendingDigitalSignature', 'Digital signing') },
                                  { value: 'pending_signing', label: t('documents.statuses.pendingSigning', 'Physical signing') },
                                  { value: 'no_signing', label: t('documents.statuses.noSigning', 'No signing') },
                                ]}
                                searchDisabled
                                compact
                                hideClear
                              />
                            </Box>
                          )}
                        </Flex>
                      )
                    })}
                  </VStack>
                  </Box>
                </Box>
              )}
            </VStack>
          </CollapsibleSection>

          <CollapsibleSection
            title={t('workforce.people.sections.paths') || t('navigation.paths') || 'Paths'}
            defaultOpen={sectionsOpen.paths}
            badge={selectedPathList.length > 0 ? String(selectedPathList.length) : undefined}
          >
            <VStack spacing="16px" align="stretch">
              <Box>
                <Flex align="flex-start" justify="space-between" gap="12px" mb="12px" flexWrap="wrap">
                  <Box>
                    <Text fontSize="12px" fontWeight="600" color="solid.500" mb="4px">
                      {t('workforce.people.documents.pathsTitle') || 'Assign Paths'}
                    </Text>
                    <Text fontSize="11px" color="solid.400">
                      {t('workforce.people.documents.pathsHint') || 'Pick published paths to assign right after this employee is created.'}
                    </Text>
                  </Box>
                </Flex>

                <SearchInput
                  key={pathSearchResetKey}
                  onSearch={(term) => setPathSearchTerm(term.trim())}
                />

                <Box
                  mt="12px"
                  borderRadius="12px"
                  bg="white"
                  maxH="280px"
                  overflowY="auto"
                  onScroll={handlePathScroll}
                >
                  <VStack spacing="8px" align="stretch">
                    {availablePaths.map((path) => {
                      const pathId = String(path.id)
                      const isSelected = Boolean(selectedPaths[pathId])
                      const participantCount = Array.isArray(path.participants) ? path.participants.length : 0

                      return (
                        <Flex
                          key={pathId}
                          align="flex-start"
                          gap="12px"
                          p="12px"
                          borderRadius="12px"
                          border="1px solid"
                          borderColor={isSelected ? 'primary.100' : 'solid.100'}
                          bg={isSelected ? 'primary.0' : 'white'}
                          _hover={{
                            bg: isSelected ? 'primary.0' : 'solid.25',
                            borderColor: isSelected ? 'primary.100' : 'solid.200',
                          }}
                          cursor="pointer"
                          onClick={() => togglePathSelection(path, !isSelected)}
                        >
                          <Checkbox
                            mt="2px"
                            isChecked={isSelected}
                            colorScheme="purple"
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => togglePathSelection(path, e.target.checked)}
                          />
                          <Box flex="1" minW={0}>
                            <Flex align="center" justify="space-between" gap="10px" mb="4px">
                              <Text fontSize="13px" fontWeight="600" color="solid.900" noOfLines={1}>
                                {path.title}
                              </Text>
                              <Tag
                                size="sm"
                                flexShrink={0}
                                borderRadius="999px"
                                bg="solid.25"
                                color="solid.600"
                                borderWidth="1px"
                                borderColor="solid.100"
                              >
                                <TagLabel>
                                  {t('paths.labels.participantCount', { count: participantCount }) || `${participantCount} participants`}
                                </TagLabel>
                              </Tag>
                            </Flex>
                            {path.description && (
                              <Text fontSize="11px" color="solid.500" noOfLines={2}>
                                {path.description}
                              </Text>
                            )}
                          </Box>
                        </Flex>
                      )
                    })}

                    {!pathTemplatesQuery.isLoading && availablePaths.length === 0 && (
                      <Box p="20px">
                        <Text fontSize="12px" color="solid.500" textAlign="center">
                          {t('workforce.people.documents.pathsEmpty') || 'No published paths found.'}
                        </Text>
                      </Box>
                    )}

                    {(pathTemplatesQuery.isLoading || pathTemplatesQuery.isFetchingNextPage) && (
                      <Flex justify="center" py="14px">
                        <Spinner size="sm" color="solid.500" />
                      </Flex>
                    )}
                  </VStack>
                </Box>
              </Box>
            </VStack>
          </CollapsibleSection>
          </ModalBody>
        <ModalFooter {...modal.footer}>
          <Button {...button.secondary} onClick={handleClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            {...button.primary}
            onClick={handleSubmit}
            isLoading={createEmployee.isPending}
            spinner={<Spinner color="solid.500" />}
          >
            {t('common.actions.add')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default AddEmployeesModal
